import * as React from 'react';
import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { useAppSelector } from '../../../../../@types/redux';
import { PillButton, ConfirmationDialog, ControlledTextField, ProjectDialog, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { CustomChangeEvent } from '../../../../../@types/helper';
import { Card, Typography } from '@mui/material';
import { upsertArrayElement } from '../../../../../services/appFunctionsService';
import { CustomTable } from '../../../../../components/table/CustomTableWrapper';
import { IPackFormValues } from '../../../../../@types/model/masterData/pack/packFormValues';
import { yupResolver } from '@hookform/resolvers/yup';
import PackModelHelper from '../../../../../@types/model/masterData/pack/packModelHelper';
import PackBillOfMaterialEditForm from './PackBillOfMaterialEditForm';
import { IPackBillOfMaterialFormValues } from '../../../../../@types/model/masterData/packBillOfMaterial/packBillOfMaterialFormValues';
import { ArrayHelper } from '@zz2/zz2-ts-utilities';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';
import OptionType from '../../../../../@types/model/optionType';

interface IPackFormProps {
    isLoading : boolean;
    initialValues : IPackFormValues;
    onSubmit : (values : IPackFormValues) => void;
    onClose : () => void;
}

const PackForm = (props : IPackFormProps) : React.ReactElement => {
    const commodities = useAppSelector(x => x.masterData.commodities);
    const packCategories = useAppSelector(x => x.masterData.packCategories);
    const sizes = useAppSelector(x => x.masterData.sizes);

    const [isBomDialogOpen, setIsBomDialogOpen] = useState<boolean>(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
    const [bomEntryToDelete, setBomEntryToDelete] = useState<IPackBillOfMaterialFormValues | null>(null);

    const { handleSubmit, control, formState: { isValid, isDirty }, watch, getValues, trigger, setValue } = useForm<IPackFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(PackModelHelper.formSchema()),
        mode: 'onChange',
    });

    const noCartons = watch('numberOfOuterPackagingOnAPallet');

    // Individually watch all fields that influence computed values and preview so re-renders happen on any change
    const hasInnerPack = watch('hasInnerPack');
    const isIntakePack = watch('isIntakePack');
    const nettWeightForm = watch('outerNettWeight');
    const unitNettWeightForm = watch('innerNettWeight');
    const packagingWeightForm = watch('outerPackagingWeight');
    const noUnitsPerCartonForm = watch('numberOfInnersPerOuter');
    const unitPackagingWeightForm = watch('innerPackagingWeight');
    const billOfMaterialsForm = watch('billOfMaterials');
    const unitGrossWeightForm = watch('innerGrossWeight');

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    // Effect for balancing weights
    useEffect(() => {
        const inputNettWeight = Number(!hasInnerPack ? nettWeightForm : unitNettWeightForm);
        const packagingWeight = Number(packagingWeightForm);
        const noUnitsPerCarton = Number(noUnitsPerCartonForm);
        const unitPackagingWeight = Number(unitPackagingWeightForm);

        const unitGrossWeightCalc = hasInnerPack ? Number((inputNettWeight || 0) + (unitPackagingWeight || 0)) : undefined;
        const nettWeightCalc = Number(hasInnerPack ? ((inputNettWeight || 0) * (noUnitsPerCarton || 0)) : inputNettWeight);
        const grossWeightCalc = Number((hasInnerPack ? ((unitGrossWeightCalc || 0) * (noUnitsPerCarton || 0)) : (nettWeightCalc || 0)) + packagingWeight);

        // Use setValue with shouldValidate: false to avoid triggering validation loops
        setValue('outerGrossWeight', Number(grossWeightCalc.toFixed(3)), { shouldDirty: true, shouldValidate: false });
        if (hasInnerPack) {
            setValue('innerGrossWeight', Number(unitGrossWeightCalc?.toFixed(3)), { shouldDirty: true, shouldValidate: false });
            setValue('outerNettWeight', Number(nettWeightCalc.toFixed(3)), { shouldDirty: true, shouldValidate: false });
        } else {
            // Ensure nettWeight is updated directly if not hasInnerPack
            setValue('outerNettWeight', Number(nettWeightCalc.toFixed(3)), { shouldDirty: true, shouldValidate: false });
        }

    }, [hasInnerPack, nettWeightForm, unitNettWeightForm, packagingWeightForm, noUnitsPerCartonForm, unitPackagingWeightForm]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    // Restrict typing to digits and separators only for numeric fields
    const handleNumericKeyDown = (e : React.KeyboardEvent) => {
        const allowedControlKeys = [
            'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Enter',
        ];
        if (allowedControlKeys.includes(e.key)) return;
        if ((e as React.KeyboardEvent<HTMLInputElement>).ctrlKey || (e as React.KeyboardEvent<HTMLInputElement>).metaKey) {
            const key = e.key.toLowerCase();
            if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
        }
        const isDigit = e.key >= '0' && e.key <= '9';
        const isDot = e.key === '.';
        const isComma = e.key === ',';
        const isSeparator = isDot || isComma;

        // If comma pressed, normalize to '.' and enforce single separator
        if (isComma) {
            const input = e.target as HTMLInputElement;
            const currentValue = input.value;
            const selectionStart = input.selectionStart ?? currentValue.length;
            const selectionEnd = input.selectionEnd ?? currentValue.length;
            const valueWithoutSelection = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd);
            const hasExistingSeparator = valueWithoutSelection.includes('.') || valueWithoutSelection.includes(',');
            e.preventDefault();
            if (hasExistingSeparator) return;
            input.setRangeText('.', selectionStart, selectionEnd, 'end');
            input.dispatchEvent(new Event('input', { bubbles: true }));
            return;
        }

        // Block second separator: allow at most one of '.' or ',' combined
        if (isDot) {
            const input = e.target as HTMLInputElement;
            const currentValue = input.value;
            const selectionStart = input.selectionStart ?? currentValue.length;
            const selectionEnd = input.selectionEnd ?? currentValue.length;
            const valueWithoutSelection = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd);
            const hasExistingSeparator = valueWithoutSelection.includes('.') || valueWithoutSelection.includes(',');
            if (hasExistingSeparator) {
                e.preventDefault();
                return;
            }
        }

        if (!isDigit && !isSeparator) {
            e.preventDefault();
        }
    };

    // Normalize and sanitize pasted values: allow digits and one '.' only
    const handleNumericPaste = (e : React.ClipboardEvent) => {
        const input = e.target as HTMLInputElement | HTMLTextAreaElement;
        const pasteText = e.clipboardData.getData('text');
        if (pasteText === undefined || pasteText === null) return;
        e.preventDefault();

        const currentValue = input.value;
        const selectionStart = input.selectionStart ?? currentValue.length;
        const selectionEnd = input.selectionEnd ?? currentValue.length;
        const valueWithoutSelection = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd);

        // Normalize commas to dots and remove invalid characters
        const normalized = pasteText.replace(/,/g, '.');
        let sanitized = normalized.replace(/[^0-9.]/g, '');

        // Collapse multiple dots in the pasted chunk to a single one
        const firstDotIndex = sanitized.indexOf('.');
        if (firstDotIndex !== -1) {
            sanitized = sanitized.slice(0, firstDotIndex + 1) + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
        }

        // If existing value (excluding selection) already has a separator, drop any dot in the paste
        const hasExistingSeparator = valueWithoutSelection.includes('.') || valueWithoutSelection.includes(',');
        if (hasExistingSeparator) {
            sanitized = sanitized.replace(/\./g, '');
        }

        if (!sanitized) return;
        input.setRangeText(sanitized, selectionStart, selectionEnd, 'end');
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const getNumberOfObjects = (isBottom : boolean) => {
        let count = 0;
        const children = [];
        const numUnits = Number(noUnitsPerCartonForm);

        if (!numUnits || numUnits <= 0) {
            return;
        }
        const cappedUnits = Math.min(numUnits, 38);
        if (isBottom) {
            count = Math.ceil(cappedUnits / 2);
        } else {
            count = Math.floor(cappedUnits / 2);
        }
        for (let i = 0; i < count; i++) {
            children.push(
                <div className={'flx1 p5 ml5 mr5 jcc aic bcs br5 h30'}>
                </div>,
            );
        }
        return children;
    };

    const openBomDialog = () => setIsBomDialogOpen(true);
    const closeBomDialog = () => {
        setIsBomDialogOpen(false);
    };

    const addNewMaterial = (values : IPackBillOfMaterialFormValues) => {
        const formValues = getValues();
        const currentList = formValues.billOfMaterials;
        const updatedList = ArrayHelper.addElement(currentList, values, 'end');
        setValue('billOfMaterials', updatedList, { shouldDirty: true, shouldValidate: true });
    };

    const getTotalPrice = (rowGuid : string) => {
        const row = billOfMaterialsForm.find(x => x.guid === rowGuid);
        let totalPrice = 0;
        if (row) {
            totalPrice = row.units * row.pricePerUnit;
        }
        return totalPrice;
    };

    const openDeletePopup = (row : IPackBillOfMaterialFormValues) => {
        setBomEntryToDelete(row);
        setIsDeleteConfirmationOpen(true);
    };

    const closeDeletePopup = () => {
        setBomEntryToDelete(null);
        setIsDeleteConfirmationOpen(false);
    };

    const removeBillEntry = () => {
        if (!bomEntryToDelete) return;

        const updatedRow = { ...bomEntryToDelete, isActive: false };
        const currentBom = billOfMaterialsForm;
        const newBillOfMaterialsList = upsertArrayElement(currentBom, updatedRow, x => x.guid === updatedRow.guid);

        setValue('billOfMaterials', newBillOfMaterialsList ?? [], { shouldDirty: true, shouldValidate: true });
        closeDeletePopup();
    };

    const onHasInnerPackChange = (e : CustomChangeEvent, checked : boolean) : void => {
        if (!checked) {
            setValue('innerGrossWeight', 0, { shouldDirty: true });
            setValue('innerNettWeight', 0, { shouldDirty: true });
            setValue('innerPackagingWeight', 0, { shouldDirty: true });
            setValue('numberOfInnersPerOuter', 0, { shouldDirty: true });

            trigger();
        }
    };

    const onIsIntakePackChange = (e : CustomChangeEvent, checked : boolean) : void => {
        if (!checked) {
            setValue('numberOfStackedUnits', 0, { shouldDirty: true, shouldValidate: true });
        }
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const commodityOptions = useMemo(() => {
        return commodities
            .filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [commodities]);

    const categoryOptions = useMemo(() => {
        return packCategories
            .filter(x => x.isActive)
            .map(OptionType.fromPackCategory);
    }, [packCategories]);

    const sizeOptions = useMemo(() => {
        return sizes
            .filter(x => x.isActive)
            .map(OptionType.fromDataModel);
    }, [sizes]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)} className={'fdc p20 hfill'}>
                <div className={'fdr flx1'}>
                    <div className={'fdc flx1 mr20 mt8'}>
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'code'}
                            label={'Code'}
                            control={control}
                            defaultValue={props.initialValues.code}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'description'}
                            label={'Description'}
                            control={control}
                            defaultValue={props.initialValues.description}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'numberOfOuterPackagingOnAPallet'}
                            label={`No.${hasInnerPack ? ' Outer' : ''} Cartons`}
                            control={control}
                            defaultValue={props.initialValues.numberOfOuterPackagingOnAPallet.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'outerPackagingWeight'}
                            label={`${hasInnerPack ? 'Outer ' : ''}Packaging Weight`}
                            control={control}
                            defaultValue={props.initialValues.outerPackagingWeight.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'outerGrossWeight'}
                            label={`${hasInnerPack ? 'Outer ' : ''}Gross Weight`}
                            control={control}
                            defaultValue={props.initialValues.outerGrossWeight.toString()}
                            disabled
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'outerNettWeight'}
                            label={`${hasInnerPack ? 'Product' : ''} Nett Weight ${hasInnerPack ? 'in an outer' : ''}`}
                            control={control}
                            defaultValue={props.initialValues.outerNettWeight.toString()}
                            disabled={hasInnerPack}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdc flx1 mr20 mt8'}>
                        <ControlledTextField
                            disabled={!hasInnerPack}
                            className={'wfill mb20 h50'}
                            name={'numberOfInnersPerOuter'}
                            label={'No. Inner Units'}
                            control={control}
                            defaultValue={props.initialValues.numberOfInnersPerOuter?.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            disabled={!hasInnerPack}
                            className={'wfill mb20 h50'}
                            name={'innerPackagingWeight'}
                            label={'Inner Packaging Weight'}
                            control={control}
                            defaultValue={props.initialValues.innerPackagingWeight?.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            className={'wfill mb20 h50'}
                            name={'innerGrossWeight'}
                            label={'Inner Gross Weight'}
                            control={control}
                            defaultValue={props.initialValues.innerGrossWeight?.toString()}
                            disabled
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            disabled={!hasInnerPack}
                            className={'wfill mb20 h50'}
                            name={'innerNettWeight'}
                            label={'Inner Nett Weight'}
                            control={control}
                            defaultValue={props.initialValues.innerNettWeight?.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                        <ControlledTextField
                            disabled={!isIntakePack}
                            className={'wfill mb20 h50'}
                            name={'numberOfStackedUnits'}
                            label={'No. Stacked Units'}
                            control={control}
                            defaultValue={props.initialValues.numberOfStackedUnits?.toString()}
                            onKeyDown={handleNumericKeyDown}
                            onPaste={handleNumericPaste}
                            size={'small'}
                        />
                    </div>
                    <div className={'fdc flx1'}>
                        <ControlledMuiAutoCompleteSelect
                            className={'h50 mb20'}
                            name={'commodity'}
                            label={'Commodity'}
                            control={control}
                            options={commodityOptions}
                            defaultValues={props.initialValues.commodity}
                        />
                        <ControlledMuiAutoCompleteSelect
                            className={'h50 mb20'}
                            name={'category'}
                            label={'Category'}
                            control={control}
                            options={categoryOptions}
                            defaultValues={props.initialValues.category}
                        />
                        <ControlledMuiAutoCompleteSelect
                            className={'h50 mb20'}
                            name={'sizes'}
                            label={'Sizes'}
                            control={control}
                            options={sizeOptions}
                            isMulti
                            defaultValues={props.initialValues.sizes}
                        />
                        <div className={'fdr mt10'}>
                            <div className={'mr20'}>
                                <ControlledLabelledCheckBox
                                    name={'hasInnerPack'}
                                    label={'Has Inner Pack'}
                                    control={control}
                                    defaultValue={props.initialValues.hasInnerPack}
                                    onChange={onHasInnerPackChange}
                                />
                            </div>
                            <div className={'mr20'}>
                                <ControlledLabelledCheckBox
                                    name={'isIntakePack'}
                                    label={'Is Intake Pack'}
                                    control={control}
                                    defaultValue={props.initialValues.isIntakePack}
                                    onChange={onIsIntakePackChange}
                                />
                            </div>
                            <div className={'mr20'}>
                                <ControlledLabelledCheckBox
                                    name={'isActive'}
                                    label={'Is Active'}
                                    control={control}
                                    defaultValue={props.initialValues.isActive}
                                />
                            </div>
                        </div>
                        <div className={'flx1'}/>
                        <div className={'fdr wfill mb10'}>
                            <div className={'flx1'}/>
                            <PillButton
                                disabled={props.isLoading}
                                className={'ml15 pl20 pr20 h35'}
                                text={'Bill Of Materials'}
                                color={'secondary'}
                                onClick={openBomDialog}
                            />
                        </div>
                    </div>
                </div>

                <div className={'flx1 fdc ml10 mr10'}>
                    <div className={'fdr wfill bcg1'}>
                        <div className={'cp pt10 pb10 pl10 fw700'}>
                        Carton Preview
                        </div>
                    </div>
                    <div className={'fdr hfill wfill jcc aic'}>
                        <div className={'fdr wfill aife jcfe pr10 posr'} >
                            <img src={`${ASSET_BASE}/assets/images/pallet_image.svg`}/>
                            <div className={'posa post50 w427'}>
                                <div className={'fdr pl20 pr20'}>
                                    {getNumberOfObjects(false)}
                                </div>
                            </div>
                            <div className={'posa post95 w427'}>
                                <div className={'fdr pl20 pr20'}>
                                    {getNumberOfObjects(true)}
                                </div>
                            </div>
                        </div>
                        <div className={'fdr wfill pl10'}>
                            <div className={'fdc jcc aic'}>
                                <Typography>Total Cartons on pallet:</Typography>
                                <div className='h30 br5 pl10 pr10 jcc aic mt10 mb20 fw500 cw bcp'>{noCartons}</div>
                                <Typography>Weight per inner (kg):</Typography>
                                <div className='h30 br5 pl10 pr10 jcc aic mt10 mb20 fw500 bcs'>{unitGrossWeightForm ? Number(unitGrossWeightForm).toFixed(3) : 0}</div>
                            </div>
                            <div className={'fdc jcc aic'}>
                                <Typography>Total inners per carton:</Typography>
                                <div className='h30 br5 pl10 pr10 jcc aic mt10 mb20 fw500 bcs'>{noUnitsPerCartonForm ? noUnitsPerCartonForm : 0}</div>
                                <Typography>Total inners weight per carton (kg):</Typography>
                                <div className='h30 br5 pl10 pr10 jcc aic mt10 mb20 fw500 cw bcp'>{(unitGrossWeightForm && noUnitsPerCartonForm) ? (Number(unitGrossWeightForm) * Number(noUnitsPerCartonForm)).toFixed(3) : 0}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'fdr flx1 aic jcfe'}>
                    <Button 
                        className={'cpd mr20'}
                        onClick={props.onClose} 
                        type={'reset'}>
                        CANCEL
                    </Button>
                    <PillButton
                        className={'pl30 pr30'}
                        text={'SAVE'}
                        size={'small'}
                        type={'submit'}
                        color={'secondary'}
                        disabled={ !isDirty || !isValid || props.isLoading }
                    />
                </div>
                {isBomDialogOpen &&
                    <ProjectDialog
                        title={'Manage Bill Of Material'}
                        fullWidth
                        maxWidth={'lg'}
                        isOpen={isBomDialogOpen}
                        disableBackdropClick
                        onClose={closeBomDialog}>
                        <div className={'fdc wfill p20'}>
                            <PackBillOfMaterialEditForm addNewMaterial={addNewMaterial}/>
                            <Card className={'fdc'}>
                                <CustomTable<IPackBillOfMaterialFormValues>
                                    enableDeleting={(row : IPackBillOfMaterialFormValues) => row.isActive}
                                    deleteFunction={openDeletePopup}
                                    enableFiltering
                                    enableSorting
                                    columns={[
                                        { title: 'Material', field: 'material.label', enableFiltering: true, enableSorting: true, width: 400 },
                                        { title: 'Number of Units', field: 'units', width: 200 },
                                        { title: 'Price Per Unit (in Rand) ', field: 'pricePerUnit', width: 260 },
                                        { title: 'Total Price (in Rand)', field: 'guid', formatFunction: getTotalPrice, width: 240 },
                                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                                    ]}
                                    rowHeight={48}
                                    rows={billOfMaterialsForm}
                                    pageHeight={200}
                                    isActive={(row : IPackBillOfMaterialFormValues) => row.isActive}
                                />
                            </Card>
                        </div>
                    </ProjectDialog >
                }
                <ConfirmationDialog
                    title={'Inactivate Bill Entry'}
                    description={'Are you sure you would like to set the following item to inactive?'}
                    open={isDeleteConfirmationOpen}
                    onAccept={removeBillEntry}
                    onClose={closeDeletePopup}
                    dialogType={'red'}
                />
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default PackForm;
